import type paper from "paper";
import {
  pieceStitchLines,
  projectConstructionLines,
  type Project,
  type StitchLine,
  type Vec2,
} from "./Project";
import {
  effectivePitch,
  isStitchLoop,
  stitchHolePlacements,
  stitchLineLength,
  stitchWorkingWindow,
} from "./stitching";
import { usesCornerLayout } from "./stitchLayout";

/**
 * Construction-anchored stitch grid (#199 Phase 3). A seam traced from a
 * construction line (`constructionOutline`) snaps its holes to a grid defined on
 * that line — holes at `k·pitch` measured from the SOURCE construction line's own
 * arc origin. Every seam traced from the same line joins one group: the longest
 * leads, and each follower takes the leader's chisel (shape, size, slant, pitch)
 * and lands its holes on the leader's grid (#1105 — before that, a group also
 * required the same pitch, and a follower whose pitch drifted left silently).
 * They line up hole-for-hole where they overlap (a partial arc) or all the way
 * around (a shared closed loop). No pairing record: the match is emergent.
 *
 * This pass stamps the derived `StitchLine.gridPhase` (the local arc offset of
 * the first grid hole); `stitchHolePlacements` consumes it. Pure-ish: needs Paper
 * for arc-length geometry, so it runs in the commit pipeline alongside
 * `deriveConstructionOutlines`. Memoized — returns the same project ref when no
 * phase changed. A count-matched seam (`holeCount`, owned by a manual match) is
 * skipped so the two mechanisms never fight.
 */

const modPos = (x: number, p: number) => (p > 0 ? ((x % p) + p) % p : 0);

function subpathsOf(cp: paper.CompoundPath): paper.Path[] {
  return cp.children && cp.children.length > 0
    ? (cp.children as paper.Path[])
    : [cp as unknown as paper.Path];
}

/** Everything needed to place one traced seam on the shared grid, or null when
 *  it doesn't qualify / its source geometry is unavailable. `key` groups seams
 *  that line up (the same source line); `gridOrigin` is the
 *  construction-arc position of the seam's own working-area START, used to pick
 *  and anchor the group's leader. */
interface SeamGrid {
  key: string;
  /** Construction arc of the seam's start point on its source line. */
  s0: number;
  /** +1 / −1: does the seam's local arc run with or against the line's arc? */
  dir: number;
  pitch: number;
  /** Total seam length — the longest seam in a group leads the grid. */
  length: number;
  /** Construction arc of the seam's working-area start (its Start handle). */
  gridOrigin: number;
  /** The source subpath the seam traces — where a corner-layout leader's holes
   *  are projected so a follower can find them (#1107). */
  sub: paper.Path;
}

/** How far a leader's hole may sit from a follower's source subpath and still
 *  be on it — float noise, not a search radius (#1107). */
const ON_SOURCE_TOL_MM = 0.05;

function gridInfoFor(
  line: StitchLine,
  subpathFor: (id: string, pathIndex: number) => paper.Path | null,
  scope: paper.PaperScope,
): SeamGrid | null {
  const outline = line.constructionOutline;
  if (!outline || outline.segments.length === 0) return null;
  // A count-matched seam is governed by its manual StitchMatch — leave it.
  if (line.holeCount != null) return null;
  if (line.points.length < 2) return null;

  const seg = outline.segments[0]!;
  const sub = subpathFor(seg.sourceId, seg.pathIndex);
  if (!sub) return null;
  const L = sub.length;
  if (!(L > 0)) return null;

  const p0 = line.points[0]!;
  const p1 = line.points[1]!;
  const s0 = sub.getNearestLocation(new scope.Point(p0.x, p0.y))?.offset;
  const s1 = sub.getNearestLocation(new scope.Point(p1.x, p1.y))?.offset;
  if (s0 == null || s1 == null) return null;

  // Local direction along the source line (does the seam's arc run with or
  // against the construction-line arc?). Resolve the wrap on a closed source by
  // taking the shorter signed step between the two adjacent seam points.
  let d = s1 - s0;
  if (sub.closed) {
    if (d > L / 2) d -= L;
    else if (d < -L / 2) d += L;
  }
  const dir = d >= 0 ? 1 : -1;

  const pitch = effectivePitch(line);
  if (!(pitch > 0)) return null;

  const total = stitchLineLength(line);
  const { lo } = stitchWorkingWindow(line, total);
  // The leader's first hole — what `gridOrigin` anchors the shared grid to — sits
  // at its working-area start. For a closed loop that start is measured from the
  // seam's Start-of-path origin, so fold `startOffset` in: moving a loop leader's
  // start-of-path then shifts `gridOrigin`, rotating every follower with it (#692).
  // Open seams ignore `startOffset` (they pin their ends), so it's left out.
  const startLocal = lo + (isStitchLoop(line) ? (line.startOffset ?? 0) : 0);
  return {
    key: seg.sourceId,
    s0,
    dir,
    pitch,
    length: total,
    gridOrigin: s0 + dir * startLocal,
    sub,
  };
}

/** Local phase that lands `seam`'s holes on the grid anchored at `gridOrigin`
 *  (construction arc) — the first local x ≥ 0 with `s0 + dir·x ≡ gridOrigin`
 *  (mod pitch). */
function phaseFor(seam: SeamGrid, gridOrigin: number, pitch: number): number {
  return modPos(seam.dir * (gridOrigin - seam.s0), pitch);
}

/** The chisel a follower takes from its leader (#1105): the leader's shape,
 *  size, slant and pitch, so the two rows are punched with the same iron and
 *  the follower's holes land on the leader's pitch. */
const CHISEL_KEYS = [
  "shape",
  "size",
  "slantAngle",
  "pitch",
  "pitchMode",
] as const;
type Chisel = Pick<StitchLine, (typeof CHISEL_KEYS)[number]>;

function chiselOf(line: StitchLine): Chisel {
  return {
    shape: line.shape,
    size: line.size,
    slantAngle: line.slantAngle,
    pitch: line.pitch,
    pitchMode: line.pitchMode,
  };
}

function sameChisel(line: StitchLine, chisel: Chisel): boolean {
  return CHISEL_KEYS.every((k) => line[k] === chisel[k]);
}

export function deriveConstructionStitchGrid(
  project: Project,
  scope: paper.PaperScope,
): Project {
  // Fast path: nothing traced and nothing to clear.
  let relevant = false;
  for (const piece of project.pieces) {
    for (const l of pieceStitchLines(piece)) {
      if (l.constructionOutline || l.gridPhase != null || l.gridMain) {
        relevant = true;
        break;
      }
    }
    if (relevant) break;
  }
  if (!relevant) return project;

  const lineById = new Map(
    projectConstructionLines(project).map((l) => [l.id, l]),
  );
  const compoundCache = new Map<string, paper.CompoundPath | null>();
  const compoundFor = (id: string): paper.CompoundPath | null => {
    if (compoundCache.has(id)) return compoundCache.get(id)!;
    const cl = lineById.get(id);
    let cp: paper.CompoundPath | null = null;
    if (cl) {
      try {
        cp = new scope.CompoundPath({ pathData: cl.pathData, insert: false });
      } catch {
        cp = null;
      }
    }
    compoundCache.set(id, cp);
    return cp;
  };
  const subpathFor = (id: string, pathIndex: number): paper.Path | null => {
    const cp = compoundFor(id);
    return cp ? (subpathsOf(cp)[pathIndex] ?? null) : null;
  };

  // Pass 1 — compute each seam's grid info and, per source-line group, count
  // the members and remember the LEADER: the longest seam, whose working-area
  // Start anchors the shared grid. A lone seam isn't anchored at all — with no
  // partner to line up with, the grid would only make its handles show/hide
  // holes instead of moving them, so it places holes normally.
  const candidate = new Map<string, SeamGrid>();
  const keyCount = new Map<string, number>();
  const leader = new Map<string, SeamGrid>();
  const leaderId = new Map<string, string>();
  const leaderLine = new Map<string, StitchLine>();
  for (const piece of project.pieces) {
    for (const l of pieceStitchLines(piece)) {
      const info = gridInfoFor(l, subpathFor, scope);
      if (!info) continue;
      candidate.set(l.id, info);
      keyCount.set(info.key, (keyCount.get(info.key) ?? 0) + 1);
      const cur = leader.get(info.key);
      // Longest wins; ties keep the first seen (stable project order).
      if (!cur || info.length > cur.length) {
        leader.set(info.key, info);
        leaderId.set(info.key, l.id);
        leaderLine.set(info.key, l);
      }
    }
  }

  // A leader laid out corner-aware (#691) spaces each run evenly between its
  // pins, so its holes are NOT on the pitch grid `gridPhase` describes — a
  // follower placed on the grid drifts off them hole by hole and the rows no
  // longer meet (#1107). Its real hole positions, as points in space, are what
  // the followers must copy instead.
  // Which of them are PIVOT holes travels with them (#1105): the leader punches
  // those round, so its partner holes on a follower must be round too.
  const leaderHoles = new Map<string, LeaderHole[]>();
  for (const [key, line] of leaderLine) {
    if ((keyCount.get(key) ?? 0) < 2 || !usesCornerLayout(line)) continue;
    leaderHoles.set(
      key,
      stitchHolePlacements(line).map((p) => ({
        position: p.position,
        corner: p.corner === true,
      })),
    );
  }

  // Pass 2 — stamp `gridPhase` on the FOLLOWERS of each group (the offset that
  // lands their holes on the leader's grid), and mark the leader `gridMain`. The
  // leader gets NO `gridPhase`: it IS the grid origin, so it places holes plainly
  // from its own working-area Start — dragging the leader's Start moves its first
  // hole (and the whole shared row re-derives to follow), while a follower's
  // Start just windows which of the shared holes it punches. Stamping the leader
  // too would make it window against its own (and, mid-drag, stale) grid — the
  // very bug this avoids. Memoized so an unchanged commit returns the same ref.
  let changed = false;
  const pieces = project.pieces.map((piece) => {
    const lines = pieceStitchLines(piece);
    if (lines.length === 0) return piece;
    let pieceChanged = false;
    const nextLines = lines.map((l) => {
      const info = candidate.get(l.id);
      const lead = info ? leader.get(info.key) : undefined;
      const inGroup = !!(info && lead && (keyCount.get(info.key) ?? 0) >= 2);
      const isLeader = inGroup && leaderId.get(info!.key) === l.id;
      // Followers carry the phase; the leader carries only `gridMain`.
      const phase =
        inGroup && !isLeader
          ? phaseFor(info!, lead!.gridOrigin, lead!.pitch)
          : null;
      const chisel =
        phase != null ? chiselOf(leaderLine.get(info!.key)!) : null;
      const fromLeader = phase != null ? leaderHoles.get(info!.key) : undefined;
      const handed = fromLeader
        ? followerHoleOffsets(info!, fromLeader, lead!.pitch, scope)
        : null;
      const holes = handed?.holes ?? null;
      const corners =
        handed && handed.corners.length > 0 ? handed.corners : null;
      const curPhase = l.gridPhase ?? null;
      const curMain = l.gridMain ?? false;
      const samePhase =
        phase == null
          ? curPhase == null
          : curPhase != null && Math.abs(phase - curPhase) < 1e-9;
      if (
        samePhase &&
        isLeader === curMain &&
        sameHoles(holes, l.gridHoles) &&
        sameHoles(corners, l.gridCornerHoles) &&
        (chisel == null || sameChisel(l, chisel))
      )
        return l;
      pieceChanged = true;
      // Rebuild from a clean base (every flag stripped), then re-add what
      // applies: `gridMain` for the leader, `gridPhase` (+ `gridHoles` under a
      // corner-layout leader) for a follower.
      const {
        gridPhase: _p,
        gridMain: _m,
        gridHoles: _h,
        gridCornerHoles: _c,
        ...rest
      } = l;
      if (isLeader) return { ...rest, gridMain: true as const };
      if (phase != null)
        return holes
          ? {
              ...rest,
              ...chisel,
              gridPhase: phase,
              gridHoles: holes,
              ...(corners ? { gridCornerHoles: corners } : {}),
            }
          : { ...rest, ...chisel, gridPhase: phase };
      return rest;
    });
    if (!pieceChanged) return piece;
    changed = true;
    return { ...piece, stitchLines: nextLines };
  });

  for (const cp of compoundCache.values()) cp?.remove();
  return changed ? { ...project, pieces } : project;
}

/** One of a corner-layout leader's holes, and whether it is a pivot hole. */
interface LeaderHole {
  position: Vec2;
  corner: boolean;
}

/** How close a follower's end must be to one of its leader's ends to count as
 *  the same end (#1106) — both come from the same construction arc, so a shared
 *  end agrees to float noise. */
const SAME_END_TOL_MM = 0.05;

/**
 * Newly created seams that land in a grid group as FOLLOWERS (#1106): an end
 * that isn't one of the leader's ends is where the follower was clipped to its
 * part — mid-row, not the end of the stitching — so it gets a working-area
 * inset of 0 there and its row runs right up to that edge in step with the
 * leader's. An end that IS one of the leader's keeps the default inset, as the
 * leader's end does, so the two still meet.
 *
 * Creation-time only: it sets `startInset` / `endInset` on the seams named in
 * `newStitchIds` and nothing else, so the user can still move those handles
 * afterwards. Runs the grid derive to learn who leads; the derive is
 * idempotent, so handing its result on to the commit pipeline is harmless.
 * Open followers only — a loop's inset already defaults to 0.
 */
export function zeroFollowerInsetsAtClippedEnds(
  project: Project,
  newStitchIds: ReadonlySet<string>,
  scope: paper.PaperScope,
): Project {
  const derived = deriveConstructionStitchGrid(project, scope);
  const lines = derived.pieces.flatMap((p) => pieceStitchLines(p));
  const leaderByKey = new Map<string, StitchLine>();
  for (const l of lines) {
    const key = l.gridMain ? gridGroupKey(l) : null;
    if (key) leaderByKey.set(key, l);
  }
  const near = (a: Vec2, b: Vec2) =>
    Math.abs(a.x - b.x) < SAME_END_TOL_MM &&
    Math.abs(a.y - b.y) < SAME_END_TOL_MM;
  let changed = false;
  const pieces = derived.pieces.map((piece) => {
    let pieceChanged = false;
    const next = pieceStitchLines(piece).map((l) => {
      if (!newStitchIds.has(l.id) || l.gridPhase == null) return l;
      if (isStitchLoop(l) || l.points.length < 2) return l;
      const key = gridGroupKey(l);
      const lead = key ? leaderByKey.get(key) : undefined;
      if (!lead || lead.points.length < 2) return l;
      const leadEnds = [lead.points[0]!, lead.points[lead.points.length - 1]!];
      const atLeadEnd = (p: Vec2) => leadEnds.some((e) => near(p, e));
      const startClipped = !atLeadEnd(l.points[0]!);
      const endClipped = !atLeadEnd(l.points[l.points.length - 1]!);
      if (!startClipped && !endClipped) return l;
      pieceChanged = true;
      return {
        ...l,
        ...(startClipped ? { startInset: 0 } : {}),
        ...(endClipped ? { endInset: 0 } : {}),
      };
    });
    if (!pieceChanged) return piece;
    changed = true;
    return { ...piece, stitchLines: next };
  });
  return changed ? { ...derived, pieces } : derived;
}

/** Where the leader's holes fall along a follower, as local arc offsets from
 *  the follower's first point (#1107), plus which of those are the leader's
 *  pivot holes (#1105). Each hole is projected onto the shared source subpath
 *  and mapped through the same `s0` / `dir` frame `phaseFor` uses; holes off
 *  the subpath or beyond the follower's ends are dropped. Null when none land
 *  on the follower; an open follower that runs past the leader continues at
 *  the leader's pitch out there (#1203). */
function followerHoleOffsets(
  seam: SeamGrid,
  leaderHoles: LeaderHole[],
  pitch: number,
  scope: paper.PaperScope,
): { holes: number[]; corners: number[] } | null {
  const L = seam.sub.length;
  const out: number[] = [];
  const corners: number[] = [];
  for (const { position: h, corner } of leaderHoles) {
    const pt = new scope.Point(h.x, h.y);
    const loc = seam.sub.getNearestLocation(pt);
    if (!loc || loc.point.getDistance(pt) > ON_SOURCE_TOL_MM) continue;
    let x = seam.dir * (loc.offset - seam.s0);
    if (seam.sub.closed) x = modPos(x, L);
    if (x < -1e-6 || x > seam.length + 1e-6) continue;
    const d = Math.round(Math.max(0, Math.min(seam.length, x)) * 1e6) / 1e6;
    out.push(d);
    if (corner) corners.push(d);
  }
  // None of the leader's holes is on this seam — the two share a source line
  // but don't overlap (#1203). There is nothing to copy, so the seam keeps the
  // shared pitch grid (`gridPhase`) instead of punching no holes at all.
  if (out.length === 0) return null;
  const asc = (a: number, b: number) => a - b;
  out.sort(asc);
  // Past the leader's reach the seam has no holes to copy either, so the row
  // carries on at the shared pitch from the leader's last hole on each side,
  // rather than stopping where the leader does (#1203). An open seam only: on a
  // loop the leader's arc can wrap, and "past its end" has no single side.
  if (!seam.sub.closed && pitch > 0) {
    const lo = out[0]!;
    const hi = out[out.length - 1]!;
    const step = (d: number) => Math.round(d * 1e6) / 1e6;
    for (let d = hi + pitch; d <= seam.length + 1e-6; d += pitch) {
      out.push(step(Math.min(seam.length, d)));
    }
    for (let d = lo - pitch; d >= -1e-6; d -= pitch) {
      out.push(step(Math.max(0, d)));
    }
    out.sort(asc);
  }
  return { holes: out, corners: corners.sort(asc) };
}

function sameHoles(a: number[] | null, b: number[] | undefined): boolean {
  if (a == null || b == null) return a == null && b == null;
  return a.length === b.length && a.every((v, i) => Math.abs(v - b[i]!) < 1e-6);
}

/** The group key a seam aligns on — its source construction line (#1105). Must
 *  match `gridInfoFor`'s `key` so the UI groups exactly as the derive pass did.
 *  Null when the seam isn't a candidate for grid alignment. */
export function gridGroupKey(line: StitchLine): string | null {
  const src = line.constructionOutline?.segments[0]?.sourceId;
  if (!src) return null;
  if (!(effectivePitch(line) > 0)) return null;
  return src;
}

export interface StitchRef {
  pieceId: string;
  stitchId: string;
}

export interface StitchGridGroup {
  /** The leader seam (longest, `gridMain`) whose Start anchors the shared grid,
   *  or null if somehow absent. */
  leader: StitchRef | null;
  /** Every co-aligned seam — the leader and all followers — in project order. */
  members: StitchRef[];
}

/** The grid-alignment group a seam belongs to (#199 Phase 3 UI): the set of
 *  seams that snap to the same shared construction grid as `stitchId`, plus
 *  which one leads. Returns null when the seam isn't grid-aligned (no partner /
 *  not construction-linked). Pure — reads only the stamped `gridPhase` /
 *  `gridMain` plus the source line, so the panels can call it directly. */
export function stitchGridGroupFor(
  project: Project,
  stitchId: string,
): StitchGridGroup | null {
  let target: StitchLine | undefined;
  for (const piece of project.pieces) {
    for (const l of pieceStitchLines(piece)) {
      if (l.id === stitchId) target = l;
    }
  }
  // A seam is in a group if it carries the follower offset (`gridPhase`) OR it's
  // the leader (`gridMain`, which has no phase since it owns the grid origin).
  const inGrid = (l: StitchLine) => l.gridPhase != null || l.gridMain === true;
  if (!target || !inGrid(target)) return null;
  const key = gridGroupKey(target);
  if (!key) return null;

  const members: StitchRef[] = [];
  let leader: StitchRef | null = null;
  for (const piece of project.pieces) {
    for (const l of pieceStitchLines(piece)) {
      if (!inGrid(l) || gridGroupKey(l) !== key) continue;
      const ref = { pieceId: piece.id, stitchId: l.id };
      members.push(ref);
      if (l.gridMain) leader = ref;
    }
  }
  return members.length >= 2 ? { leader, members } : null;
}
